/* This is Sponsored Text Offers component for the flex page, but the initial implementation works on mobile only.
  A rethought and updated version of this component, which also works properly on the desktop is called SponsoredOffers.
  In the future, during proper refactoring and finishing conversion of desktop pages on React the current component
  should not be used anymor, will be removed and replaced with SponsoredOffers instead. */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchPlacements } from 'core/actions/ocapi';
import isEmpty from 'lodash/isEmpty';
import { convertDateTimeToETTimezone } from 'core/utils/helpers/date';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import SectionTitle from 'core/modules/SectionTitle/SectionTitle';
import OfferTextItem from 'core/modules/OfferTextItem/OfferTextItem';
import FeaturedDealMobileRounded
  from 'core/modules/FeaturedDeals/components/FeaturedDeal/FeaturedDealMobileRounded/FeaturedDealMobileRounded';
import { selectPageName } from 'core/selectors/app';
import { selectIsMobileMQ } from 'core/modules/MediaQueries/selectors';
import { triggerSponsoredTextOffersViewed } from './analytics/types';
import {
  selectSponsoredTextOffersConfig, makeSelectSponsoredTextOffersData,
  makeSelectSponsoredTextOffersDataToDisplay, makeSelectSponsoredTextOffersIsLoaded,
  makeSelectSponsoredTextOffersIsLoading,
} from './selectors';

import './SponsoredTextOffers.scss';
import { sortMerchantsOfferList } from './util';
import FeaturedDeal from '../FeaturedDeals/components/FeaturedDeal/FeaturedDeal';

function SponsoredTextOffers({
  favorites = {}, showFavoritesBundle, content_group_id,
  category_id, namespace = 'default', customParams,
  section_title: sectionTitle, pill,
}) {
  const dispatch = useDispatch();
  const selectSponsoredTextOffersData = useMemo(makeSelectSponsoredTextOffersData, []);
  const selectSponsoredTextOffersDataToDisplay = useMemo(makeSelectSponsoredTextOffersDataToDisplay, []);
  const selectSponsoredTextOffersIsLoaded = useMemo(makeSelectSponsoredTextOffersIsLoaded, []);
  const selectSponsoredTextOffersIsLoading = useMemo(makeSelectSponsoredTextOffersIsLoading, []);

  const data = useSelector(state => selectSponsoredTextOffersData(state, namespace));
  const dataToDisplay = useSelector(state => selectSponsoredTextOffersDataToDisplay(state, namespace));
  const config = useSelector(selectSponsoredTextOffersConfig);
  const pageName = useSelector(selectPageName);
  const isLoaded = useSelector(state => selectSponsoredTextOffersIsLoaded(state, namespace));
  const isLoading = useSelector(state => selectSponsoredTextOffersIsLoading(state, namespace));
  const isMobileMQ = useSelector(selectIsMobileMQ);

  const [sentViewGAEvent, setSentViewGAEvent] = useState(false);

  const { api: { params, options }, shouldUseMobileRoundedView } = config;
  const { sectionId } = options;

  const forYouData = sortMerchantsOfferList(data, favorites).slice(0, 4);

  const callFetchPlacements = () => {
    const actualParams = { ...params, ...customParams };
    const paramsWithContentGroup = { content_group_id, category_id, ...actualParams };

    if (!paramsWithContentGroup.content_group_id && !paramsWithContentGroup.content_type_id) {
      return false; // content_group_id or content_type_id param is required for /placements API endpoint
    }

    dispatch(fetchPlacements(paramsWithContentGroup, { ...options, namespace }));
    return true;
  };

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      callFetchPlacements();
    }
  }, [isLoaded, isLoading]);

  useEffect(() => {
    if (pageName === 'foryou' && isLoaded && isEmpty(forYouData)) {
      showFavoritesBundle();
    }
  }, [data, favorites, isLoaded]);

  useEffect(() => {
    if (pageName === 'foryou' && isLoaded && !sentViewGAEvent && !isEmpty(forYouData)) {
      setSentViewGAEvent(true);
      dispatch(triggerSponsoredTextOffersViewed(forYouData.length));
    }
  }, [data, favorites, isLoaded]);

  const getSponsoredTextOfferContent = (sectionId) => {
    if (pageName === 'foryou') {
      if (isEmpty(forYouData)) {
        return null;
      }

      return forYouData?.map((dataItem) => (
        <FeaturedDeal
          offer={dataItem}
          expires={convertDateTimeToETTimezone(dataItem.expires, 'America/New_York').format('MM/DD/YYYY')}
          key={`${dataItem.id}`}
          config={config}
          customExpireText="Valid thru"
          use88Image
          useAddToFavorite
        />
      ));
    }

    if (isMobileMQ && shouldUseMobileRoundedView) {
      return (
        <div className="mn_featuredDeals">
          {dataToDisplay.map(dataItem => (
            <FeaturedDealMobileRounded
              key={dataItem.id}
              offer={dataItem}
              config={config}
            />
          ))}
        </div>
      );
    }

    return dataToDisplay.map((dataItem) => (
      <OfferTextItem key={dataItem.id} dataItem={dataItem} sectionId={sectionId} className="mn_sponsoredTextOffer" />
    ));
  };

  if (isEmpty(dataToDisplay)) {
    return null;
  }

  return (
    <div className="mn_sponsoredTextOffers" data-test="sponsored-text-offers" data-title={sectionTitle}>
      <SectionTitle title={sectionTitle} pill={pill} />
      {getSponsoredTextOfferContent(sectionId)}
    </div>
  );
}

export default (props) => <ComponentErrorBoundary><SponsoredTextOffers {...props} /></ComponentErrorBoundary>;
